/**
 * What-If Panel — analysis workspace.
 *
 * Pure what-if analysis: Safe to Miss, Scenario Comparison, Pace Projection,
 * Back-Calculation, Calendar-Aware Planning, and Trend Analytics.
 * Notification preferences live in Settings → General; Calendar Sync and LMS
 * Connect live in Settings → Integrations.
 */

import React, { useState, useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useAppStore } from '@/store';
import { SafeToMissPanel } from '../SafeToMiss/SafeToMissPanel';
import { ScenarioComparison } from '../ScenarioComparison/ScenarioComparisonPanel';
import { CalendarAwarePlanner } from './CalendarAwarePlanner';
import { TrendAnalytics } from '../TrendAnalytics/TrendAnalyticsPanel';
import { projectPace } from '@abs/shared-logic';
import { backCalculateMaxAbsences, backCalculateForAttendanceTarget } from '@abs/shared-logic';
import { computeTotalSessions } from '@abs/shared-logic';
import { computeEffectiveAbsences } from '@abs/shared-logic';
import { selectReasonAwarePace, selectSmartSkipPlan } from '@/store/selectors';
import { CollapsiblePanel, StatCard } from '../shared';
import type { AbsenceEntry } from '@abs/shared-types';

interface WhatIfPanelProps {
  courseId: string;
}

export function WhatIfPanel({ courseId }: WhatIfPanelProps): React.ReactElement | null {
  const course = useAppStore((s) => s.courses.find((c) => c.id === courseId));
  const policy = useAppStore((s) => s.policies.find((p) => p.id === course?.policyId));
  const entries = useAppStore((s) => s.entries.filter((e: AbsenceEntry) => e.courseId === courseId));

  const [targetGrade, setTargetGrade] = useState('');
  const [targetType, setTargetType] = useState<'grade' | 'attendance'>('grade');
  const [attendanceTarget, setAttendanceTarget] = useState(90);

  const reasonPace = useAppStore((s) => selectReasonAwarePace(s, courseId));
  const smartSkip = useAppStore((s) =>
    course?.baseGrade !== null && targetGrade ? selectSmartSkipPlan(s, courseId, targetGrade) : null,
  );

  // Sessions are derived from course patterns (pure, no hooks).
  const { sessions, total } = computeTotalSessions(
    course?.meetingPatterns ?? [], course?.sessionOverrides ?? [],
    course?.startDate ?? '', course?.endDate ?? '',
  );

  // Back-calculation
  const maxAffordable = useMemo(() => {
    if (!course?.baseGrade || !targetGrade || !policy) return null;
    return backCalculateMaxAbsences(
      targetGrade,
      course.gradingScale,
      course.baseGrade,
      policy,
      entries,
      total,
    );
  }, [course, policy, entries, targetGrade, total]);

  if (!course || !policy) return null;

  // Pace projection
  const pace = projectPace(entries, policy, sessions);

  // Attendance-threshold targeter (TODO-09)
  const attendanceEff = computeEffectiveAbsences(entries, policy);
  const affordableByAttendance = backCalculateForAttendanceTarget(total, attendanceEff.totalPenaltyAbsences, attendanceTarget);

  return (
    <div className="what-if-panel" role="region" aria-label="What-If Analysis">
      <h3>What-If Analysis</h3>

      {/* Safe to Miss */}
      <CollapsiblePanel title="Safe to Miss?" defaultOpen={true}>
        <SafeToMissPanel course={course} />
      </CollapsiblePanel>

      {/* Scenario Comparison */}
      <CollapsiblePanel title="Scenario Comparison" defaultOpen={true}>
        <ScenarioComparison course={course} />
      </CollapsiblePanel>

      {/* Pace Projection */}
      <CollapsiblePanel title="Attendance Pace" defaultOpen={false}>
        <div className="what-if-panel__pace">
          <StatCard value={`${(pace.missRate * 100).toFixed(1)}%`} label="Miss Rate" />
          <StatCard value={pace.projectedTotal} label="Projected Total Absences" />
          <StatCard
            value={pace.sessionsPerAbsence === Infinity ? '∞' : pace.sessionsPerAbsence}
            label="Sessions per Absence"
          />
          <p className="stat-caption">Miss rate = penalty absences ÷ past sessions; projected total extends that rate over the full term; sessions-per-absence is its inverse.</p>
          {reasonPace && (
            <p className="stat-caption">Excluding illness/family: {(reasonPace.controllableOnly.missRate * 100).toFixed(1)}% miss rate ({reasonPace.uncontrollableCount} uncontrollable absences).</p>
          )}
          {pace.leadsToFail && (
            <div className="what-if-panel__pace-warning" role="alert">
              <AlertTriangle aria-hidden="true" /> At your current pace, you will exceed your absence budget by end of term.
            </div>
          )}
        </div>
      </CollapsiblePanel>

      {/* Back-Calculation */}
      {course.baseGrade !== null && (
        <CollapsiblePanel title="How Many Can I Miss?" defaultOpen={false}>
          <div className="what-if-panel__backcalc">
            <div className="what-if-panel__target-toggle" role="radiogroup" aria-label="Target type">
              <label>
                <input
                  type="radio"
                  name={`target-type-${courseId}`}
                  checked={targetType === 'grade'}
                  onChange={() => setTargetType('grade')}
                />{' '}
                Letter grade
              </label>
              <label>
                <input
                  type="radio"
                  name={`target-type-${courseId}`}
                  checked={targetType === 'attendance'}
                  onChange={() => setTargetType('attendance')}
                />{' '}
                Attendance %
              </label>
            </div>

            {targetType === 'grade' ? (
              <>
                <label title="Pick the lowest letter grade you want to keep; the result is how many more penalty absences fit before dropping below it.">
                  Target letter grade
                  <select value={targetGrade} onChange={(e) => setTargetGrade(e.target.value)}>
                    <option value="">— Select —</option>
                    {course.gradingScale.map((g) => (
                      <option key={g.letter} value={g.letter}>{g.letter} ({g.minPercentage}%)</option>
                    ))}
                  </select>
                </label>
                {maxAffordable !== null && (
                  <div className="what-if-panel__backcalc-result" role="status">
                    You can miss <strong>{maxAffordable}</strong> more session{maxAffordable !== 1 ? 's' : ''} and still maintain a {targetGrade}.
                    {maxAffordable === 0 && <span className="what-if-panel__backcalc-zero"> You are already at or below this target.</span>}
                  </div>
                )}

                {/* Target-Aware Smart Skip Recommender (TODO-12) */}
                {smartSkip && targetGrade && (
                  <div className="what-if-panel__smart-skip">
                    {smartSkip.affordable === 0 ? (
                      <p className="stat-caption">No sessions are safe to skip without dropping below {targetGrade}.</p>
                    ) : (
                      <>
                        <p className="stat-caption"><strong>Safest to skip:</strong></p>
                        <ul>
                          {smartSkip.safeToSkip.map((s) => (
                            <li key={s.date}>{s.date} — {s.reason}</li>
                          ))}
                        </ul>
                        {smartSkip.doNotMiss.length > 0 && (
                          <>
                            <p className="stat-caption"><strong>Do not miss:</strong></p>
                            <ul>
                              {smartSkip.doNotMiss.slice(0, 5).map((s) => (
                                <li key={s.date}>{s.date} — {s.reason}</li>
                              ))}
                            </ul>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <label>
                  Target attendance %
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={attendanceTarget}
                    onChange={(e) => setAttendanceTarget(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                  />
                </label>
                <div className="what-if-panel__backcalc-result" role="status">
                  To stay ≥ {attendanceTarget}% attendance, you can miss <strong>{affordableByAttendance}</strong> more session{affordableByAttendance !== 1 ? 's' : ''}.
                </div>
              </>
            )}
          </div>
        </CollapsiblePanel>
      )}

      {/* Calendar-Aware Planning (Phase 4) */}
      <CollapsiblePanel title="Plan Against Calendar" defaultOpen={false}>
        <CalendarAwarePlanner course={course} />
      </CollapsiblePanel>

      {/* Trend Analytics */}
      <CollapsiblePanel title="Attendance Trends" defaultOpen={false}>
        <TrendAnalytics course={course} />
      </CollapsiblePanel>
    </div>
  );
}
